import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { getCalendarEvents } from '../../../../../services/CalendarService.js';
import { getPriorityMeta, normalizePriority } from '../../../../../utils/eventPriority.js';

// One page of a day. Small enough that the first screen arrives quickly on a
// heavy date, large enough that most days need no second request.
const PAGE_SIZE = 15;

// The colour a row is filed under. It follows the kind of thing the row is,
// not its priority -- the priority is a tag of its own further along, and a
// reader should be able to tell a holiday from a batch class without reading
// either label.
const TYPE_TONES = {
  holiday: { key: 'holiday', label: 'Holiday', color: '#FF6B35' },
  event: { key: 'event', label: 'Event', color: '#DC3545' },
  mock: { key: 'mock', label: 'Mock interview', color: '#E5A000' },
  batch: { key: 'batch', label: 'Batch class', color: '#28A745' },
  free: { key: 'free', label: 'Free slot', color: '#20C997' },
};

const toneOf = (ev) => {
  const props = ev?.extendedProps || {};
  if (props.type === 'free_slot') return TYPE_TONES.free;
  if (props.type === 'batch_daily') return TYPE_TONES.batch;
  if (props.isHoliday || props.category === 'Holiday') return TYPE_TONES.holiday;
  if (props.category === 'Mock Interview') return TYPE_TONES.mock;
  return TYPE_TONES.event;
};

// A row's own time, in the shape the reader expects for its kind: all-day
// things say so, a batch class carries the slot the backend resolved.
const timeOf = (ev) => {
  const props = ev?.extendedProps || {};
  if (props.type === 'batch_daily' && props.batchTime) return props.batchTime;
  if (ev?.allDay) return 'All day';
  if (!ev?.start) return '';
  const start = moment(ev.start);
  const end = ev.end ? moment(ev.end) : null;
  return end ? `${start.format('h:mm A')} – ${end.format('h:mm A')}` : start.format('h:mm A');
};

// Everything worth showing once a row is opened, as label/value pairs. Empty
// values are dropped rather than printed as "NA", so a sparse event stays
// short instead of listing what it does not have.
const detailsOf = (ev) => {
  const props = ev?.extendedProps || {};
  const data = props.data || {};
  const trainers =
    typeof props.trainers === 'object' ? props.trainers?.name : props.trainers;

  const pairs =
    props.type === 'batch_daily'
      ? [
          ['Batch code', props.batchCode],
          ['Course', props.course?.name],
          ['Trainer', trainers],
          ['Room', props.venue],
          ['Mode', data.mode || props.mode],
          [
            'Day',
            props.dayNumber && props.totalDays
              ? `${props.dayNumber} of ${props.totalDays}`
              : null,
          ],
          ['Meeting link', data.meetingLink],
          ['Meeting ID', data.meetingId],
        ]
      : [
          ['Trainer', props.trainerName || props.trainerNames?.join(', ')],
          ['Batches', props.batchName || props.batchNames?.join(', ')],
          ['Room', props.classroomNumber],
          ['Online link', data.onlineLink],
          ['Address', data.offlineAddress],
          ['Organiser', data.organizerName],
          ['Email', data.organizerEmail],
          ['Phone', data.organizerPhone],
        ];

  const prose = [
    ['Description', data.description || props.description],
    ['Agenda', data.agenda],
    ['Highlights', data.highlights],
  ];

  return {
    pairs: pairs.filter(([, v]) => String(v ?? '').trim()),
    prose: prose.filter(([, v]) => String(v ?? '').trim()),
  };
};

const isLink = (value) => /^https?:\/\//i.test(String(value || '').trim());

/** The identity a row is opened and closed by. One definition, so the
    "expand everything" pass below cannot drift from the row it renders. */
const rowKey = (ev, i) => `${ev?.id || ev?.title || 'row'}-${ev?.start || i}`;

const DayEventsModal = ({
  date,
  filters,
  // The number printed on the cell that opened this: how many of the date's
  // entries match the filters the grid is under. Two jobs -- it fills the
  // header before the first response lands, and it is the figure the gap
  // clause reports once the real list arrives. The list itself is not
  // filtered by type, so this is usually the smaller of the two.
  total,
  filtersActive = false,
  canAdd = false,
  onAddEvent,
  onOpenEvent,
  onClose,
}) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [failed, setFailed] = useState(false);

  // Which request the answer belongs to. A slow first page must not land on
  // top of the results of a search typed after it.
  const seq = useRef(0);

  /* Appending reads the rows already in hand. Through a ref, so `load`
     does not take `rows` as a dependency and rebuild on every page. */
  const rowsRef = useRef([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const load = useCallback(
    async (wanted, append) => {
      const mine = ++seq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setFailed(false);

      try {
        /* `filters` already carries whose calendar this is -- the grid
           passes the same batchId list it was fetched with -- so this
           asks the server the narrowed question and pages the narrowed
           answer. Nothing is sieved after the fact, which is what let
           paging work again: a page of 15 is 15 rows the reader can
           see, and `hasMore` means what it says. */
        const res = await getCalendarEvents({
          ...filters,
          viewStart: moment(date).startOf('day').toISOString(),
          viewEnd: moment(date).endOf('day').toISOString(),
          /* The same date again, as a plain YYYY-MM-DD, and it is not
             redundant: viewStart/viewEnd are INSTANTS, and east of
             Greenwich one local day spans two UTC dates. In IST, 1 Sep
             runs 2026-08-31T18:30Z to 2026-09-01T18:29Z -- so the batch
             expansion, which walks UTC dates via toISOString(), generated
             31 Aug's sessions as well and this list opened on a day
             holding seven classes that belong to the day before.

             startDateFilter/endDateFilter are ISO DATES, and the backend
             already clamps the expansion to them. Pinning both to the
             clicked date is what keeps a day list to its own day. */
          startDateFilter: date,
          endDateFilter: date,
          // hidePastEvents comes through in `filters`, from the grid. It
          // used to be forced to false here, on the reasoning that a day
          // list should show its day in full -- but that made the list
          // answer a different question from the number on the cell that
          // opened it, and the number is the promise. They move together
          // now: hide past in the grid and a past day reads 0 and opens
          // empty; turn it off and both show everything.
          includeFreeSlots: false,
          page: wanted,
          limit: PAGE_SIZE,
          search: term || undefined,
        });
        if (mine !== seq.current) return;

        const list = Array.isArray(res?.data) ? res.data : [];
        const next = append ? [...rowsRef.current, ...list] : list;
        setRows(next);
        /* pagination.total is trustworthy again: the server counted the
           same narrowed day it returned rows from. While the browser was
           doing the narrowing this said 7 over a list of 2. */
        setPageInfo(res?.pagination || null);
        setPage(wanted);
        /* Every row opens with the day. A column of collapsed titles
           makes the reader click each one to find out what it is, and
           reading the day is the whole reason the date was opened. */
        setOpen(new Set(next.map((ev, i) => rowKey(ev, i))));
      } catch (_) {
        if (mine !== seq.current) return;
        if (!append) setRows([]);
        setFailed(true);
      } finally {
        if (mine === seq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [date, filters, term],
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  // The search runs on the server, so it is held back until typing settles.
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const day = moment(date);
  const shown = rows.length;
  const found = pageInfo?.total ?? shown;

  /* The header counts what this modal's own query returned, not what the
     grid cell says.

     `total` is still used before the first response lands, so the header
     is not blank on open. */
  const headline = pageInfo || rows.length ? found : (total ?? found);

  /* A cell counts what matches the grid's filters; this list shows every
     type on the date, because once you have picked a day the useful thing
     is the whole day. So the two legitimately differ, and the header has
     to account for the difference -- a list quietly showing more than the
     cell promised reads as a bug, which is precisely what it used to be.

     Only said when a filter is actually on and the numbers really differ:
     with nothing filtered they always agree and the clause would be
     noise. */
  const settled = Boolean(pageInfo) && !loading && !term;
  const showGap =
    settled &&
    filtersActive &&
    Number.isFinite(total) &&
    total !== found;

  return createPortal(
    <div
      className="pg-modal cal-day-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cal-day-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pg-modal-panel pg-modal-panel--wide cal-day-panel">
        <header className="pg-modal-head cal-day-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">{day.format('dddd')}</span>
            <h3 className="pg-modal-title cal-day-title" id="cal-day-title">
              {day.format('D MMMM YYYY')}
            </h3>
            <p className="cal-day-count">
              {loading
                ? 'Looking...'
                : headline === 0
                  ? term
                    ? 'Nothing matches that'
                    : 'Nothing on this day'
                  : `${headline} ${headline === 1 ? 'entry' : 'entries'}${term ? ' found' : ''}`}
              {showGap && (
                <span className="cal-day-gap">
                  {total === 0
                    ? 'none match your filters'
                    : `${total} match your filters`}
                </span>
              )}
            </p>
          </div>

          <div className="cal-day-head-actions">
            {canAdd && (
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => onAddEvent(date)}
              >
                Add event
              </button>
            )}
            <button
              type="button"
              className="pg-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </header>

        <div className="cal-day-search">
          <input
            type="search"
            className="pg-input pg-input--search"
            placeholder="Search this day by title, trainer, batch, room or organiser..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="pg-modal-body cal-day-body">
          {loading ? (
            <div className="att-picklist-foot">
              <span className="pg-spinner att-spinner" aria-hidden="true" />
              <span>Loading this day...</span>
            </div>
          ) : failed && shown === 0 ? (
            <div className="cal-day-empty">
              <p>That did not load. Close this and try again.</p>
            </div>
          ) : shown === 0 ? (
            <div className="cal-day-empty">
              <p>
                {term
                  ? 'No entry on this day matches what you typed.'
                  : 'Nothing is scheduled on this day.'}
              </p>
            </div>
          ) : (
            <ul className="cal-day-list">
              {rows.map((ev, i) => {
                const tone = toneOf(ev);
                const key = rowKey(ev, i);
                const isOpen = open.has(key);
                const priority = normalizePriority(ev?.extendedProps?.priority);
                const meta = getPriorityMeta(priority);
                const hasPriority = ev?.extendedProps?.type === 'event';
                const { pairs, prose } = detailsOf(ev);
                const empty = pairs.length === 0 && prose.length === 0;

                return (
                  <li
                    className={`cal-day-row is-${tone.key}${isOpen ? ' is-open' : ''}`}
                    key={key}
                    style={{ '--cal-row-tone': tone.color, animationDelay: `${Math.min(i, 8) * 26}ms` }}
                  >
                    <button
                      type="button"
                      className="cal-day-row-head"
                      onClick={() => !empty && toggle(key)}
                      aria-expanded={isOpen}
                      disabled={empty}
                    >
                      <span className="cal-day-when">{timeOf(ev)}</span>

                      <span className="cal-day-main">
                        <span className="cal-day-name">{ev.title || 'Untitled'}</span>
                        <span className="cal-day-tags">
                          <span className="cal-tag cal-tag--type">{tone.label}</span>
                          {hasPriority && (
                            <span
                              className="cal-tag cal-tag--prio"
                              style={{
                                color: meta.color,
                                background: meta.bg,
                                borderColor: meta.border,
                              }}
                            >
                              {meta.label}
                            </span>
                          )}
                          {ev?.extendedProps?.category &&
                            ev.extendedProps.category !== 'Holiday' &&
                            ev.extendedProps.category !== 'Mock Interview' && (
                              <span className="cal-tag">{ev.extendedProps.category}</span>
                            )}
                        </span>
                      </span>

                      {!empty && (
                        <span className="cal-day-chev" aria-hidden="true">
                          ⌄
                        </span>
                      )}
                    </button>

                    {isOpen && (
                      <div className="cal-day-detail">
                        {pairs.length > 0 && (
                          <dl className="cal-day-facts">
                            {pairs.map(([label, value]) => (
                              <div className="cal-day-fact" key={label}>
                                <dt>{label}</dt>
                                <dd>
                                  {isLink(value) ? (
                                    <a
                                      href={value}
                                      target="_blank"
                                      rel="noreferrer noopener"
                                    >
                                      {value}
                                    </a>
                                  ) : (
                                    value
                                  )}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}

                        {prose.map(([label, value]) => (
                          <div className="cal-day-prose" key={label}>
                            <h6>{label}</h6>
                            <p>{value}</p>
                          </div>
                        ))}

                        {onOpenEvent && ev?.extendedProps?.type === 'event' && (
                          <div className="cal-day-row-actions">
                            <button
                              type="button"
                              className="pg-btn"
                              onClick={() => onOpenEvent(ev)}
                            >
                              Open full view
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Where the reader is in the day, and the next page. */}
          {!loading && shown > 0 && (
            <div className="att-picklist-foot">
              {loadingMore ? (
                <>
                  <span className="pg-spinner att-spinner" aria-hidden="true" />
                  <span>Loading more...</span>
                </>
              ) : pageInfo?.hasMore ? (
                <button
                  type="button"
                  className="pg-btn"
                  onClick={() => load(page + 1, true)}
                >
                  Show more ({shown} of {found})
                </button>
              ) : (
                <span>
                  All {found} {found === 1 ? 'entry' : 'entries'} shown
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DayEventsModal;
